/* eslint-disable @angular-eslint/directive-class-suffix */
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import type { OnDestroy } from '@angular/core';
import { Component, ElementRef, EventEmitter, forwardRef, HostBinding, Input, NgZone, Output, ViewChild } from '@angular/core';
import type { ControlValueAccessor } from '@angular/forms';
import { NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { MatLegacyAutocompleteTrigger as MatAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import type { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { MatLegacyAutocomplete as MatAutocomplete } from '@angular/material/legacy-autocomplete';
import type { MatLegacyChipInputEvent as MatChipInputEvent } from '@angular/material/legacy-chips';
 import type { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

// language=Angular2Html
export const template = `
    <mat-form-field appearance="outline" [ngClass]="{'ng-invalid': invalid, 'mat-form-field-invalid': invalid}">
        <mat-label *ngIf="label">{{ label }}
        <ng-container *ngIf="required"> * </ng-container>
        </mat-label>
        <mat-chip-list #chipList
                       [disabled]="disabled">

            <mat-chip
                *ngFor="let option of options"
                [removable]="removable"
                (removed)="remove(option)">
                {{ option }}
                <mat-icon matChipRemove *ngIf="removable">cancel</mat-icon>
            </mat-chip>

            <input
                [placeholder]="placeholder"
                #inputElement
                [formControl]="valueCtrl"
                [matAutocomplete]="auto"
                [matChipInputFor]="chipList"
                [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                (blur)="onBlur()"
                (matChipInputTokenEnd)="addChip($event)"
                [maxLength]="maxLength">
        </mat-chip-list>

        <mat-autocomplete #auto="matAutocomplete"
                          (optionSelected)="selected($event)">
            <mat-option *ngFor="let option of filteredOptions$ | async"
            [attr.data-testid]="option"
                        [value]="option">
                {{ option }}
            </mat-option>
        </mat-autocomplete>

        <div matSuffix>
            <ng-content select="[appSuffix]"></ng-content>
        </div>

        </mat-form-field>

    <mat-hint *ngIf="!valueCtrl.value && hint">  {{ hint }}  </mat-hint>
    <mat-hint *ngIf="valueCtrl.value">Press enter or type a comma to add</mat-hint>
`;

@Component({
    selector: 'app-chips-form-field',
    styleUrls: ['./chips-form-field.component.scss'],
    template,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ChipsFormFieldComponent),
            multi: true,
        },
    ],
})
export class ChipsFormFieldComponent implements ControlValueAccessor, OnDestroy {
    @HostBinding('class.app-form-field') readonly appFormField = true;

    @Input() label: string;
    @Input() disabled: boolean;
    @Input() placeholder: string;
    @Input() hint: string;
    @Input() required = false;
    @Input() invalid = false;
    @Input() maxLength = 255;

    removable = true;
    separatorKeysCodes: number[] = [ENTER, COMMA];
    valueCtrl = new UntypedFormControl();

    @Input() suggestions: string[] = [];
    filteredOptions$: Observable<string[]>;

    private _options: string[] = [];
    get options(): string[] {
        return this._options;
    }

    @Input()
    set options(value: string[]) {
        this._options = value ?? [];
    }

    @Output() optionsChange = new EventEmitter<string[]>();
    @Output() inputBlur = new EventEmitter<void>();

    @ViewChild('inputElement') inputElement: ElementRef<HTMLInputElement>;
    @ViewChild('auto') matAutocomplete: MatAutocomplete;
    @ViewChild('inputElement', { read: MatAutocompleteTrigger }) autocompleteEl: MatAutocompleteTrigger;


    constructor(protected ngZone: NgZone) {
        this.filteredOptions$ = this.valueCtrl.valueChanges.pipe(
            startWith(null),
            map((option: string | null) => this._filter(option))
        );
        this.ngZone.runOutsideAngular(() => window.addEventListener('scroll', this.scrollEvent, true));
    }

    private scrollEvent = (): void => {
        if (this.autocompleteEl.panelOpen) {
            this.ngZone.run(() => {
                this.autocompleteEl.updatePosition();
            });
        }
    };

    addChip(event: MatChipInputEvent): void {
        const input = event.chipInput;
        const value = event.value;

        if ((value || '').trim()) {
            this.addOption(value);
        }

        // Reset the input value
        if (input) {
            input.clear();
        }
        this.valueCtrl.setValue(null);
    }

    remove(option: string): void {
        const index = this._options.indexOf(option);

        if (index >= 0) {
            this._options.splice(index, 1);
        }
        this.optionsChange.emit(this.options);
    }

    private addOption(value: string) {
        this._options.push(value);

        this.optionsChange.emit(this.options);
    }

    selected(event: MatAutocompleteSelectedEvent): void {
        this.addOption(event.option.value);
        this.inputElement.nativeElement.value = '';
        this.valueCtrl.setValue(null);
    }

    private _filter(value: string): string[] {
        const suggestions = this.suggestions.slice().filter(suggestion => this.options.indexOf(suggestion) < 0);

        if (!value) {
            return suggestions;
        }

        const filterValue = value.toLowerCase();

        return suggestions.filter(option => option.toLowerCase().includes(filterValue));
    }

    ngOnDestroy(): void {
        window.removeEventListener('scroll', this.scrollEvent);
    }

    onChange = _ => {};

    onTouched = () => {};

    onBlur(){
        this.inputBlur.emit();
    }

    writeValue(value: string[]) {
        this._options = value;
    }

    registerOnChange(fn: (_: any) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
